/**
 * Local-storage backup tier for clinical draft saves.
 *
 * The primary draft save flow is DB-first, blob-second (see
 * universalClinicalDocumentService.saveDraft). When the DB save fails — Postgres
 * outage, network drop, or any 5xx after retries — the clinician's typed
 * content is lost unless we capture it client-side first. This util provides
 * that capture: write a snapshot to localStorage immediately before the DB
 * call, clear it after success. If the DB call fails, the snapshot survives
 * a window close / page refresh and the next mount of useDraftManager
 * recovers it automatically.
 *
 * Existing keys remain readable. New callers may supply a writerId to keep
 * separate windows from overwriting each other's pending recovery data.
 * Pending snapshots are never evicted automatically.
 */

import { safeStorage } from './safeStorage';
import { storeSnapshotImages, restoreSnapshotImages, removeUnreferencedSnapshotImages, hasInlineImages } from './draftSnapshotImages';

const KEY_PREFIX = 'evv_draft_recovery';
// Recovery window for the safety net: 24 hours.
// The snapshot is meant to bridge a failed save -> retry, which is hours-scale
// (clinician notices the error, comes back within minutes or after a meeting).
// A longer window means snapshots from completed-but-not-cleared sessions can
// pollute new documents for the same (patient, documentType) — see the Elton
// John report on 2026-05-10 where a "new" Standard DA recovered content from a
// previous session because the snapshot lingered after completion.
// completeDocument now also clears the snapshot explicitly, so a 24-hour
// window is plenty for the legitimate recovery case.
const STALE_AGE_MS = 24 * 60 * 60 * 1000;
// Deletion window is LONGER than the form-recovery window: a snapshot too old
// for silent in-form restore (>24h) may still be recoverable by the login-time
// sweeper (draftSnapshotSweeper.js), which uploads orphans to the server as
// real drafts. A clinician whose save failed Friday night logs in Monday and
// still gets their content back. Added after India Jamal's Skylar Detrick ITP
// loss (2026-06-09) where the only possible copy was a local snapshot.

export const getCurrentSnapshotOrganizationId = () => {
  try {
    const organization = JSON.parse(safeStorage.getItem('evv_organization') || '{}');
    const user = JSON.parse(safeStorage.getItem('evv_user') || '{}');
    return organization.id || user.organizationId || null;
  } catch {
    return null;
  }
};

export const isSnapshotOwnedByOrganization = (snapshot, organizationId) =>
  Boolean(snapshot?.organizationId && organizationId && snapshot.organizationId === organizationId);

const IDENTITY_FIELDS = [
  'organizationId', 'userId', 'patientId', 'waitlistEntryId', 'documentType',
  'draftId', 'programId', 'sessionDate', 'assessmentDate', 'planDate', 'startDate',
  'documentDate', 'originalSourceType', 'originalAssessmentId', 'originalUploadedDocumentId',
  'sessionId', 'sessionKey', 'documentId', 'superviseeId', 'sourceType', 'sourceId', 'addendumSourceId',
];

const firstValue = (...values) => values.find(value => value !== undefined && value !== null && value !== '') || null;

const SERVER_REVISION_FIELDS = ['lastModified', 'updatedAt'];

const DA_DOCUMENT_TYPES = new Set([
  'DIAGNOSTIC_ASSESSMENT',
  'MH_DIAGNOSTIC_ASSESSMENT',
  'STANDARD_DIAGNOSTIC_ASSESSMENT',
  'YZ_CARETAP_DIAGNOSTIC_ASSESSMENT',
]);
const DA_METADATA_FIELDS = new Set([
  'programName', 'programType',
  'organizationId', 'userId', 'patientId', 'subjectId', 'waitlistEntryId', 'documentType',
  'draftId', 'programId', 'baseRevision', 'writerId', 'storageKey', 'receipt', 'lastWrittenAt',
  'id', 'assessmentId', 'documentKey', 'assessmentType', 'formNumber', 'version', 'status', 'isCompleted',
  'completedAt', 'createdBy', 'updatedBy', 'patientName', 'clientName', 'clientId', 'clientNumber',
  'clientDob', 'clientDOB', 'patientDob', 'patientDOB', 'clientGender', 'providerType', 'providerCredentials',
  'clientAddress', 'clientInsurance', 'sessionDate', 'assessmentDate', 'documentDate', 'planDate',
  'startDate', 'sessionId', 'documentId', 'originalSourceType', 'originalAssessmentId',
  'originalUploadedDocumentId', 'sourceType', 'sourceId', 'addendumSourceId', 'superviseeId',
  'sessionStartTime', 'sessionEndTime', 'startTime', 'endTime', 'serviceLocation', 'hasTravel',
  'travelToStartTime', 'travelToEndTime', 'travelFromStartTime', 'travelFromEndTime', 'mileageTo',
  'mileageFrom', 'electronicSource', 'odometerStart', 'odometerEnd', 'lateEntryReason', 'serviceCode',
  'interviewer', 'personInterviewed', 'interviewerName', 'providerName', 'clinicianName',
  'signerProviderType', 'signerTitle', 'completedBy', 'completedById', 'createdAt', 'updatedAt', 'lastModified', 'savedAt',
]);
const DA_CLINICAL_FIELDS = new Set([
  'specifierDetails', 'supervisedProviderName',
  'presentingProblems', 'symptoms', 'symptomsOther', 'medicalPast', 'medicalCurrent', 'medicalProviders',
  'mhPreviousTreatment', 'mhComments', 'currentlyOnMedications', 'currentMedications', 'pastMedicationsExist',
  'pastMedications', 'medicationComments', 'familyMedical', 'familyMedicalComments', 'familyMentalHealth',
  'familyMentalHealthComments', 'familyDrugUse', 'familyDrugUseComments', 'housingStatus', 'clientStrengths',
  'clientVulnerabilities', 'meaningfulActivities', 'cultural', 'educationHighest', 'educationSchools',
  'currentStudent', 'legalStatus', 'legalComments', 'noTraumaHistory', 'traumaComments', 'gad7Responses',
  'phq9Responses', 'cageAidResponses', 'mentalStatus', 'risk', 'infoSources', 'participationDescription',
  'caseConceptualization', 'medicalNecessity', 'medicalNecessityNarrative', 'clinicalSummary',
  'principalDiagnosis', 'additionalDiagnoses', 'provisionalDiagnoses', 'serviceRecommendations',
  'furtherEvaluation', 'servicePreferences', 'referralFrequency', 'referralFrequencyDetail',
  'clinicalTraineeSignatureData', 'clinicalTraineeSignatureName',
  'clinicalTraineeSignatureDate', 'clinicalTraineeSignatureTimestamp', 'mhpSignatureNote',
  'nonBillableReason', 'currentLifeSituation', 'family', 'developmental', 'substanceAbuse', 'socioEconomic',
  'psychiatric', 'medical', 'maltreatmentTrauma', 'strengths', 'weaknesses', 'notes', 'appearance',
  'activityLevel', 'speech', 'streamConsciousness', 'attitudeToExaminer', 'thoughtProcess', 'thoughtContent',
  'hallucinations', 'delusions', 'ideasOfReference', 'affect', 'mood', 'memory', 'memoryType',
  'judgmentInsight', 'judgmentInsightType', 'orientation', 'historian', 'assessmentSummary', 'referral',
  'baselineMeasurements', 'differentialDx', 'diagnosisExplanation', 'cageAid', 'cageaidScore',
  'riskAssessment', 'immediateRisks', 'perceptionOfCondition', 'clientSymptoms', 'historyOfTreatment',
  'dsm5Code', 'dsm5Description', 'icd10Codes', 'specifier', 'specifierExplain', 'severity', 'severityExplain',
  'principalDiagnosisCode', 'provisionalDiagnosis', 'otherSpecifiedDisorders', 'verbalClientApproval',
  'supervisedProviderSignature', 'supervisedProviderSignatureDate', 'supervisedProviderSignatureTime',
  'supervisorSignature', 'supervisorSignatureDate',
]);
const DA_SCALAR_DEFAULTS = {
  mhPreviousTreatment: 'no', currentlyOnMedications: 'no', pastMedicationsExist: 'no', currentStudent: 'no',
  memoryType: 'immediate', judgmentInsightType: 'mild', serviceCode: '90791',
  isNonBillable: false, noTraumaHistory: false, principalDiagnosis: false, provisionalDiagnosis: false,
  otherSpecifiedDisorders: false, verbalClientApproval: false,
};

const hasNonDefaultValue = (value) => {
  if (value === null || value === undefined || value === '' || value === false || value === 0) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.some(hasNonDefaultValue);
  if (typeof value === 'object') return Object.values(value).some(hasNonDefaultValue);
  return true;
};

const isDefaultMentalStatus = value => !hasNonDefaultValue(value);
const isDefaultRiskMatrix = value => !hasNonDefaultValue(value);
const isDefaultRiskAssessment = (value) => {
  if (!value || typeof value !== 'object') return false;
  const rows = Array.isArray(value) ? value : Object.values(value);
  return rows.every(row => {
    if (!row || typeof row !== 'object') return false;
    const risk = row.risk;
    const riskIsDefault = risk === undefined || risk === null || risk === '' || risk === 'None';
    // MH DAs initialize `date` for every risk row. It is a form default when
    // no risk is selected; notes and dateOfLast remain clinical content.
    const notesAreEmpty = !hasNonDefaultValue(row.notes);
    const dateOfLastIsEmpty = !hasNonDefaultValue(row.dateOfLast);
    return riskIsDefault && notesAreEmpty && dateOfLastIsEmpty;
  });
};
const isDefaultScreening = value => Array.isArray(value)
  && value.every(answer => answer === null || answer === undefined || answer === '');
const isDefaultCageAid = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return !hasNonDefaultValue(value);
  return Object.entries(value)
    .filter(([field]) => !['score', 'yesCount'].includes(field))
    .every(([, answer]) => answer === null || answer === undefined || answer === '');
};
const isDefaultScalar = (field, value) => Object.prototype.hasOwnProperty.call(DA_SCALAR_DEFAULTS, field)
  ? value === DA_SCALAR_DEFAULTS[field]
  : false;

/**
 * Classify whether a diagnostic-assessment payload contains clinical content.
 * Unknown populated fields deliberately do not count as substantive: callers
 * must not use an unrecognized payload to justify silent restoration.
 */
export const classifyDraftClinicalContent = (documentType, formData = {}) => {
  const isDA = DA_DOCUMENT_TYPES.has(String(documentType || '').toUpperCase());
  if (!isDA) return hasNonDefaultValue(formData) ? 'unknown' : 'empty';
  let substantive = false;
  for (const [field, value] of Object.entries(formData || {})) {
    if (DA_METADATA_FIELDS.has(field)) continue;
    const isScreeningField = ['gad7Responses', 'phq9Responses', 'cageAidResponses'].includes(field);
    if (isScreeningField) {
      if (value != null && !isDefaultScreening(value)) substantive = true;
      continue;
    }
    if (field === 'cageAid') {
      if (!isDefaultCageAid(value)) substantive = true;
      continue;
    }
    if (field === 'cageaidScore' && typeof value === 'number' && Number.isFinite(value)) {
      substantive = true;
      continue;
    }
    if (!hasNonDefaultValue(value)) continue;
    if (!DA_CLINICAL_FIELDS.has(field)) return 'unknown';
    if (isDefaultScalar(field, value)) continue;
    if (field === 'mentalStatus' && isDefaultMentalStatus(value)) continue;
    if (field === 'risk' && isDefaultRiskMatrix(value)) continue;
    if (field === 'riskAssessment' && isDefaultRiskAssessment(value)) continue;
    if (field === 'cageAid' && isDefaultCageAid(value)) continue;
    substantive = true;
  }
  return substantive ? 'substantive' : 'empty';
};

const normalizeFingerprintValue = (value, field = '') => {
  if (Array.isArray(value)) {
    const normalized = value.map(item => normalizeFingerprintValue(item, field));
    if (['gad7Responses', 'phq9Responses', 'cageAidResponses'].includes(field)) {
      // Null/empty trailing answers are equivalent across older/newer form
      // initializers (`[]` versus a full unanswered question-length array).
      while (normalized.length && normalized[normalized.length - 1] === undefined) normalized.pop();
    }
    return normalized;
  }
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((result, key) => {
      const normalized = normalizeFingerprintValue(value[key], field === 'cageAid' ? field : key);
      if (normalized !== undefined) result[key] = normalized;
      return result;
    }, {});
  }
  // Older DA payloads used booleans for CAGE answers; the form normalizes them
  // to yes/no during onLoadComplete. Treat those representations identically.
  if (['cageAidResponses', 'cageAid'].includes(field)) {
    if (value === true) return 'yes';
    if (value === false) return 'no';
  }
  if (typeof value === 'string' && (field === 'date' || field === 'dateOfLast')) {
    return value.split('T')[0];
  }
  if (value === undefined || value === null || value === '') return undefined;
  return value;
};

/**
 * Stable comparison payload for protecting a hydrated DA from real clinical
 * edits. Identity, scheduling, interviewer, and other metadata are excluded;
 * meaningful clinical fields (including answered zero/false screenings and
 * risk notes/dates) remain represented.
 */
export const getDraftClinicalContentFingerprint = (documentType, formData = {}) => {
  const isDA = DA_DOCUMENT_TYPES.has(String(documentType || '').toUpperCase());
  if (!isDA) return JSON.stringify(normalizeFingerprintValue(formData));
  const clinical = Object.keys(formData || {}).sort().reduce((result, field) => {
    if (DA_METADATA_FIELDS.has(field)) return result;
    const normalized = normalizeFingerprintValue(formData[field], field);
    if (normalized !== undefined) result[field] = normalized;
    return result;
  }, {});
  return JSON.stringify(clinical);
};

/**
 * Return the server revision exactly as it was hydrated. Revision matching is
 * intentionally exact: a local copy tied to a different server revision must
 * never be silently restored over the server copy.
 */
export const getHydratedServerRevision = (server = null) => {
  if (!server || typeof server !== 'object') return firstValue(server);
  return firstValue(
    ...SERVER_REVISION_FIELDS.map(field => server[field]),
    server.metadata?.lastModified,
    server.metadata?.updatedAt,
  );
};

/**
 * Convert a hydrated server timestamp (or local snapshot lastWrittenAt) to a
 * comparable epoch value. Invalid and missing values return null.
 */
export const normalizeHydratedServerTimestamp = (value) => {
  if (value && typeof value === 'object' && !(value instanceof Date)) {
    const candidates = [
      value.lastModified,
      value.updatedAt,
      value.metadata?.lastModified,
      value.metadata?.updatedAt,
    ];
    for (const candidate of candidates) {
      const normalized = normalizeHydratedServerTimestamp(candidate);
      if (normalized !== null) return normalized;
    }
    return null;
  }
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : null;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || !value.trim()) return null;
  // Numeric strings are commonly used by test fixtures and older clients for
  // epoch timestamps; Date.parse() treats them as years instead.
  if (/^\d+(?:\.\d+)?$/.test(value.trim())) {
    const time = Number(value);
    return Number.isFinite(time) ? time : null;
  }
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : null;
};

// Short aliases make the timestamp contract easy to use at hydration sites.
export const normalizeServerTimestamp = normalizeHydratedServerTimestamp;
export const getDraftServerRevision = getHydratedServerRevision;

/**
 * Return the stable identity carried by a local snapshot. New metadata is
 * additive so snapshots written by older clients remain readable, but the
 * automatic recovery path can require every known identity component.
 */
export const getDraftSnapshotIdentity = (snapshot = {}) => {
  const data = snapshot.formData || {};
  return {
    organizationId: firstValue(snapshot.organizationId),
    userId: firstValue(snapshot.userId),
    patientId: firstValue(snapshot.patientId),
    subjectId: firstValue(snapshot.subjectId, snapshot.patientId),
    waitlistEntryId: firstValue(snapshot.waitlistEntryId, data.waitlistEntryId),
    documentType: firstValue(snapshot.documentType),
    draftId: firstValue(snapshot.draftId, data.draftId),
    programId: firstValue(snapshot.programId, data.programId),
    sessionDate: firstValue(snapshot.sessionDate, data.sessionDate),
    assessmentDate: firstValue(snapshot.assessmentDate, data.assessmentDate),
    planDate: firstValue(snapshot.planDate, data.planDate),
    startDate: firstValue(snapshot.startDate, data.startDate),
    documentDate: firstValue(snapshot.documentDate, data.documentDate),
    originalSourceType: firstValue(snapshot.originalSourceType, data.originalSourceType),
    originalAssessmentId: firstValue(snapshot.originalAssessmentId, data.originalAssessmentId),
    originalUploadedDocumentId: firstValue(snapshot.originalUploadedDocumentId, data.originalUploadedDocumentId),
    sessionId: firstValue(snapshot.sessionId, data.sessionId),
    documentId: firstValue(snapshot.documentId, data.documentId),
    baseRevision: firstValue(snapshot.baseRevision),
    superviseeId: firstValue(snapshot.superviseeId, data.superviseeId),
    sessionKey: firstValue(snapshot.sessionKey),
    sourceType: firstValue(snapshot.sourceType, data.sourceType),
    sourceId: firstValue(snapshot.sourceId, data.sourceId),
    addendumSourceId: firstValue(snapshot.addendumSourceId, data.addendumSourceId),
    writerId: firstValue(snapshot.writerId),
  };
};

/**
 * Resolve the identity of the form being edited. The caller may provide
 * explicit values for identifiers that are not part of formData (for example
 * a URL draftId or program selected by the opener).
 */
export const resolveDraftSnapshotIdentity = (identity = {}) => {
  const resolved = getDraftSnapshotIdentity({ formData: identity.formData || identity });
  return {
    ...resolved,
    ...Object.fromEntries(IDENTITY_FIELDS
      .filter(field => identity[field] !== undefined)
      .map(field => [field, firstValue(identity[field])])),
    patientId: firstValue(identity.patientId, identity.subjectId, resolved.patientId),
    subjectId: firstValue(identity.subjectId, identity.patientId, resolved.subjectId, resolved.patientId),
    waitlistEntryId: firstValue(identity.waitlistEntryId, resolved.waitlistEntryId),
    organizationId: firstValue(identity.organizationId, resolved.organizationId, getCurrentSnapshotOrganizationId()),
    userId: firstValue(identity.userId, resolved.userId, (() => {
      try {
        const user = JSON.parse(safeStorage.getItem('evv_user') || '{}');
        return user.id || user.userId || null;
      } catch { return null; }
    })()),
  };
};

const identityValue = (identity, field) => {
  if (field === 'patientId' && identity.subjectId) return identity.subjectId;
  return firstValue(identity[field]);
};

const comparableIdentityValue = (identity, field) => {
  const value = identityValue(identity, field);
  if (!value) return null;
  if (['sessionDate', 'assessmentDate', 'planDate', 'startDate', 'documentDate'].includes(field)) {
    return typeof value === 'string' ? value.split('T')[0] : value;
  }
  return String(value);
};

/**
 * Strictly match a snapshot to the currently resolved editing identity.
 * Missing identifier values never act as wildcards: if either side knows an
 * ID, both sides must know the same ID. This prevents a new/unknown draft from
 * recovering another session, program, source, tenant, user, or document.
 */
export const isDraftSnapshotCompatible = (snapshot, identity = {}, {
  writerId, includeCurrentWriter = false, allowUnknownProvenance = false,
} = {}) => {
  if (!snapshot) return false;
  const expected = resolveDraftSnapshotIdentity(identity);
  const actual = getDraftSnapshotIdentity(snapshot);
  if (!includeCurrentWriter && writerId && (actual.writerId === writerId
    || String(actual.writerId || '').startsWith(`${writerId}-`)
    || String(snapshot.storageKey || '').includes(writerId))) return false;

  const required = ['organizationId', 'userId', 'patientId', 'documentType'];
  if (required.some(field => !identityValue(expected, field) || !identityValue(actual, field))) return false;
  if (comparableIdentityValue(expected, 'patientId') !== comparableIdentityValue(actual, 'patientId')) return false;
  if (comparableIdentityValue(expected, 'waitlistEntryId') !== comparableIdentityValue(actual, 'waitlistEntryId')) return false;
  if (comparableIdentityValue(expected, 'organizationId') !== comparableIdentityValue(actual, 'organizationId')
    || comparableIdentityValue(expected, 'userId') !== comparableIdentityValue(actual, 'userId')
    || comparableIdentityValue(expected, 'documentType') !== comparableIdentityValue(actual, 'documentType')) return false;

  const samePersistedDraft = Boolean(expected.draftId && actual.draftId
    && expected.draftId === actual.draftId);
  for (const field of IDENTITY_FIELDS.filter(name => !required.includes(name))) {
    // Dates are editable content once the server has assigned a draft ID.
    // Unsaved date corrections must remain recoverable on that same draft.
    // New/legacy drafts still require date matching to prevent cross-session recovery.
    if (samePersistedDraft && CLINICAL_DATE_FIELDS.includes(field)) continue;
    const wanted = comparableIdentityValue(expected, field);
    const found = comparableIdentityValue(actual, field);
    if (Boolean(wanted) !== Boolean(found)) {
      if (allowUnknownProvenance && ['draftId', 'programId'].includes(field) && !found) continue;
      return false;
    }
    if (wanted && wanted !== found) return false;
  }
  return true;
};

/**
 * Select exactly one latest compatible snapshot. Ordering is independent of
 * localStorage insertion order: lastWrittenAt desc, then storage key and
 * receipt desc for deterministic ties. Preservation copies and the current
 * writer are excluded, while every snapshot remains stored.
 */
export const listCompatibleDraftSnapshots = (snapshots, identity = {}, options = {}) => {
  // Support both selectLatestCompatibleDraftSnapshot(snapshots, identity)
  // and selectLatestCompatibleDraftSnapshot(identity) for small callers.
  if (!Array.isArray(snapshots) && snapshots && typeof snapshots === 'object'
    && Object.keys(identity).length === 0) {
    identity = snapshots;
    snapshots = null;
  }
  const list = Array.isArray(snapshots) ? snapshots : listDraftSnapshots();
  const compatible = list.filter(snapshot =>
    isDraftSnapshotCompatible(snapshot, identity, options)
    && Number.isFinite(Number(snapshot.lastWrittenAt))
    && Date.now() - Number(snapshot.lastWrittenAt) <= STALE_AGE_MS
    && (options.includePreservation || !snapshot.isPreservationCopy)
    && (options.includeCurrentWriter || !options.writerId || !String(snapshot.storageKey || '').includes(options.writerId))
  );
  compatible.sort((a, b) => {
    const timeDiff = Number(b.lastWrittenAt) - Number(a.lastWrittenAt);
    if (timeDiff) return timeDiff;
    const keyDiff = String(b.storageKey || '').localeCompare(String(a.storageKey || ''));
    if (keyDiff) return keyDiff;
    return String(b.receipt || '').localeCompare(String(a.receipt || ''));
  });
  return compatible;
};

export const selectLatestCompatibleDraftSnapshot = (snapshots, identity = {}, options = {}) => {
  const compatible = listCompatibleDraftSnapshots(snapshots, identity, options);
  return compatible[0] || null;
};

/**
 * Determine whether a snapshot is safe for silent, automatic restoration.
 *
 * Automatic restore is stricter than the recovery-history list: the primary
 * path requires every known identity component to match, and preservation /
 * current-writer copies are always excluded. Snapshots written by newer
 * clients carry baseRevision and must match the hydrated server revision
 * exactly. A narrowly bounded fallback supports legacy copies missing only
 * draftId/programId, but only against a fully hydrated server row with an
 * exact clinical date and a strictly newer local write. With no server draft,
 * only the strict path can restore a legacy copy.
 */
const CLINICAL_DATE_FIELDS = ['sessionDate', 'assessmentDate', 'planDate', 'startDate', 'documentDate'];
const LEGACY_RELAXED_FIELDS = ['draftId', 'programId'];

const autoRestoreCandidateKind = (snapshot, identity = {}, options = {}) => {
  if (!snapshot || snapshot.isPreservationCopy) return null;
  const {
    writerId,
    serverDraftExists = false,
    serverRevision,
    hydratedServerRevision,
    serverLastModified,
    serverUpdatedAt,
    hydratedServerTimestamp,
    serverTimestamp: suppliedServerTimestamp,
    hydratedServer,
    serverData,
    documentType,
    serverBaselineContent,
    serverBaselineFormData,
    currentFormDataMatchesServerBaseline = true,
    allowLegacyFallback = true,
    now = Date.now(),
  } = options;

  const strictIdentityMatch = isDraftSnapshotCompatible(snapshot, identity, {
    writerId,
    includeCurrentWriter: false,
    allowUnknownProvenance: false,
  });
  const relaxedIdentityMatch = !strictIdentityMatch && allowLegacyFallback
    && isDraftSnapshotCompatible(snapshot, identity, {
      writerId,
      includeCurrentWriter: false,
      allowUnknownProvenance: true,
    });

  const writtenAt = normalizeHydratedServerTimestamp(snapshot.lastWrittenAt);
  if (!Number.isFinite(writtenAt) || !Number.isFinite(now) || writtenAt > now
    || now - writtenAt > STALE_AGE_MS) return null;

  const hydrated = hydratedServer || serverData;
  const rawServerRevision = firstValue(
    hydratedServerRevision,
    serverRevision,
    hydrated ? getHydratedServerRevision(hydrated) : null,
    serverLastModified,
    serverUpdatedAt,
  );
  const exactServerRevision = rawServerRevision && typeof rawServerRevision === 'object'
    && !(rawServerRevision instanceof Date)
    ? getHydratedServerRevision(rawServerRevision)
    : rawServerRevision;
  const serverTimestamp = [
    serverLastModified,
    serverUpdatedAt,
    hydratedServerTimestamp,
    suppliedServerTimestamp,
    hydrated,
    exactServerRevision,
  ]
    .map(normalizeHydratedServerTimestamp)
    .find(Number.isFinite) ?? null;
  const hasHydratedServerTimestamp = [
    hydratedServerRevision,
    serverRevision,
    serverLastModified,
    serverUpdatedAt,
    hydratedServerTimestamp,
    suppliedServerTimestamp,
  ].some(value => value !== undefined && value !== null && value !== '')
    || Boolean(hydrated);
  const baseRevision = firstValue(snapshot.baseRevision);
  const resolvedIdentity = resolveDraftSnapshotIdentity(identity);
  const hasClinicalDate = CLINICAL_DATE_FIELDS.some(field => comparableIdentityValue(resolvedIdentity, field));
  const baselineContent = serverBaselineContent
    || (serverBaselineFormData ? classifyDraftClinicalContent(documentType, serverBaselineFormData) : null);
  const isDiagnosticAssessment = DA_DOCUMENT_TYPES.has(String(documentType || '').toUpperCase());
  const emptyServerException = isDiagnosticAssessment
    && serverDraftExists
    && baselineContent === 'empty'
    && classifyDraftClinicalContent(documentType, snapshot.formData) === 'substantive'
    && currentFormDataMatchesServerBaseline !== false
    && !baseRevision
    && hasClinicalDate;

  if (strictIdentityMatch && baseRevision) {
    // A known base revision is safe only when it identifies this hydrated
    // server copy exactly. No server row/revision means there is no match.
    return exactServerRevision !== null && baseRevision === exactServerRevision ? 'strict' : null;
  }

  if (strictIdentityMatch) {
    // Legacy snapshots need a trustworthy server timestamp to establish that
    // the local write happened after hydration. A server row with no usable
    // timestamp cannot safely be compared.
    if (serverDraftExists || hasHydratedServerTimestamp) {
      return (emptyServerException || (serverTimestamp !== null && writtenAt > serverTimestamp)) ? 'strict' : null;
    }
    return 'strict';
  }

  // The compatibility helper above is history-only permissive. Automatic
  // fallback may relax exactly the two provenance fields that older clients
  // did not persist, and nothing else.
  if (!relaxedIdentityMatch || !allowLegacyFallback || baseRevision
    || !serverDraftExists || !exactServerRevision || serverTimestamp === null
    || (!emptyServerException && !(writtenAt > serverTimestamp))) return null;

  const expected = resolveDraftSnapshotIdentity(identity);
  const actual = getDraftSnapshotIdentity(snapshot);
  const relaxedFields = LEGACY_RELAXED_FIELDS.filter(field => {
    const wanted = comparableIdentityValue(expected, field);
    const found = comparableIdentityValue(actual, field);
    return wanted && !found;
  });
  // A fallback must have actually relaxed a missing provenance field; a
  // strict mismatch in any other identity field is never recoverable.
  if (!relaxedFields.length) return null;

  // Legacy fallback is date-scoped. A snapshot with no exact clinical date is
  // not attributable to the currently hydrated server draft.
  const expectedDates = CLINICAL_DATE_FIELDS
    .map(field => comparableIdentityValue(expected, field))
    .filter(Boolean);
  if (!expectedDates.length || CLINICAL_DATE_FIELDS.some(field => {
    const wanted = comparableIdentityValue(expected, field);
    const found = comparableIdentityValue(actual, field);
    return Boolean(wanted) !== Boolean(found) || (wanted && wanted !== found);
  })) return null;

  return 'legacy';
};

export const isDraftSnapshotEligibleForAutoRestore = (snapshot, identity = {}, options = {}) =>
  Boolean(autoRestoreCandidateKind(snapshot, identity, options));

const compareRecoveryCandidates = (a, b, identity) => {
  const expected = resolveDraftSnapshotIdentity(identity);
  const aIdentity = getDraftSnapshotIdentity(a.snapshot);
  const bIdentity = getDraftSnapshotIdentity(b.snapshot);
  const exactDraftA = comparableIdentityValue(aIdentity, 'draftId') === comparableIdentityValue(expected, 'draftId');
  const exactDraftB = comparableIdentityValue(bIdentity, 'draftId') === comparableIdentityValue(expected, 'draftId');
  if (exactDraftA !== exactDraftB) return exactDraftB ? 1 : -1;
  const timeDiff = Number(b.snapshot.lastWrittenAt) - Number(a.snapshot.lastWrittenAt);
  if (timeDiff) return timeDiff;
  const keyDiff = String(b.snapshot.storageKey || '').localeCompare(String(a.snapshot.storageKey || ''));
  if (keyDiff) return keyDiff;
  return String(b.snapshot.receipt || '').localeCompare(String(a.snapshot.receipt || ''));
};

/**
 * List strict, auto-restorable snapshots in newest-first order. Eligibility is
 * applied before sorting/selection so an ineligible newest copy cannot hide an
 * older eligible copy.
 */
export const listEligibleDraftSnapshotsForAutoRestore = (snapshots, identity = {}, options = {}) => {
  if (!Array.isArray(snapshots) && snapshots && typeof snapshots === 'object'
    && Object.keys(identity).length === 0) {
    identity = snapshots;
    snapshots = null;
  }
  const list = Array.isArray(snapshots) ? snapshots : listDraftSnapshots();
  const resolvedDocumentType = options.documentType || identity.documentType;
  const emptyDiagnosticBaseline = DA_DOCUMENT_TYPES.has(String(resolvedDocumentType || '').toUpperCase())
    && (options.serverBaselineContent === 'empty'
      || (options.serverBaselineFormData
        && classifyDraftClinicalContent(resolvedDocumentType, options.serverBaselineFormData) === 'empty'));
  const ranked = list
    .map(snapshot => ({ snapshot, kind: autoRestoreCandidateKind(snapshot, identity, options) }))
    .filter(candidate => candidate.kind)
    .filter(({ snapshot }) => !emptyDiagnosticBaseline
      || classifyDraftClinicalContent(resolvedDocumentType, snapshot.formData) === 'substantive');
  const strict = ranked.filter(candidate => candidate.kind === 'strict');
  if (strict.length) return strict.sort((a, b) => compareRecoveryCandidates(a, b, identity)).map(({ snapshot }) => snapshot);

  const legacy = ranked.filter(candidate => candidate.kind === 'legacy');
  // Legacy copies with neither provenance field are one stream when their
  // strict target/date identity matches. Select the newest deterministically;
  // populated identity and date conflicts were already filtered above.
  return legacy.sort((a, b) => compareRecoveryCandidates(a, b, identity)).map(({ snapshot }) => snapshot);
};

export const selectLatestEligibleDraftSnapshotForAutoRestore = (snapshots, identity = {}, options = {}) =>
  listEligibleDraftSnapshotsForAutoRestore(snapshots, identity, options)[0] || null;

// Descriptive aliases for callers that prefer the shorter recovery wording.
export const listEligibleDraftSnapshots = listEligibleDraftSnapshotsForAutoRestore;
export const selectLatestEligibleDraftSnapshot = selectLatestEligibleDraftSnapshotForAutoRestore;

// Descriptive aliases keep the selector easy to discover for callers that do
// not know the internal "DraftSnapshot" naming convention.
export const selectLatestCompatibleSnapshot = selectLatestCompatibleDraftSnapshot;
export const getLatestCompatibleDraftSnapshot = selectLatestCompatibleDraftSnapshot;
export const findLatestCompatibleDraftSnapshot = selectLatestCompatibleDraftSnapshot;

/**
 * @param {string} patientId
 * @param {string} documentType
 * @returns {string}
 */
export const getSnapshotKey = (patientId, documentType) => {
  if (!patientId || !documentType) return null;
  return `${KEY_PREFIX}:${documentType}:${patientId}`;
};

/**
 * Persist the current form state to localStorage. Safe to call on every
 * keystroke; payloads are small enough that localStorage handles it.
 *
 * @param {string} patientId
 * @param {string} documentType
 * @param {object} formData - The current form state (will be JSON-serialized)
 * @param {object} [extra] - Optional metadata: patientName, programId
 */
export const writeDraftSnapshot = (patientId, documentType, formData, extra = {}) => {
  const baseKey = getSnapshotKey(patientId, documentType);
  const session = formData?.originalAssessmentId || formData?.originalUploadedDocumentId || formData?.sessionDate || formData?.assessmentDate || formData?.planDate || formData?.startDate || extra.draftId || 'undated';
  const key = baseKey && extra.writerId ? `${baseKey}:${extra.writerId}:${encodeURIComponent(session)}` : baseKey;
  if (!key || !formData) return;
  try {
    const receipt = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const payload = JSON.stringify({
      patientId,
      subjectId: patientId,
      documentType,
      // Large images (signatures) are stored once and referenced; see
      // draftSnapshotImages.js. Readers restore them.
      formData: storeSnapshotImages(formData, window.localStorage),
      patientName: extra.patientName || formData?.patientName || formData?.clientName || null,
      programId: extra.programId || formData?.programId || null,
      organizationId: extra.organizationId || getCurrentSnapshotOrganizationId(),
      userId: JSON.parse(safeStorage.getItem('evv_user') || '{}').id || JSON.parse(safeStorage.getItem('evv_user') || '{}').userId || null,
      draftId: extra.draftId || null,
      waitlistEntryId: extra.waitlistEntryId || formData?.waitlistEntryId || null,
      sessionDate: extra.sessionDate || formData?.sessionDate || null,
      assessmentDate: extra.assessmentDate || formData?.assessmentDate || null,
      planDate: extra.planDate || formData?.planDate || null,
      startDate: extra.startDate || formData?.startDate || null,
      documentDate: extra.documentDate || formData?.documentDate || null,
      originalSourceType: extra.originalSourceType || formData?.originalSourceType || null,
      originalAssessmentId: extra.originalAssessmentId || formData?.originalAssessmentId || null,
      originalUploadedDocumentId: extra.originalUploadedDocumentId || formData?.originalUploadedDocumentId || null,
      sessionId: extra.sessionId || formData?.sessionId || null,
      documentId: extra.documentId || formData?.documentId || null,
      baseRevision: extra.baseRevision || null,
      superviseeId: extra.superviseeId || formData?.superviseeId || null,
      sessionKey: extra.sessionKey || null,
      sourceType: extra.sourceType || formData?.sourceType || null,
      sourceId: extra.sourceId || formData?.sourceId || null,
      addendumSourceId: extra.addendumSourceId || formData?.addendumSourceId || null,
      writerId: extra.writerId || null,
      isPreservationCopy: Boolean(extra.isPreservationCopy),
      receipt,
      lastWrittenAt: Date.now(),
    });
    // safeStorage intentionally swallows quota errors. Draft persistence must
    // verify the write so the form can warn when its safety net is unavailable.
    window.localStorage.setItem(key, payload);
    if (window.localStorage.getItem(key) !== payload) throw new Error('Storage unavailable');
    return { key, receipt };
  } catch {
    console.warn('[draftSnapshot] Local recovery storage unavailable');
    return null;
  }
};

/**
 * Read a snapshot. Returns null if missing, malformed, or stale.
 *
 * @param {string} patientId
 * @param {string} documentType
 * @returns {{patientId:string, documentType:string, formData:object, patientName:string|null, programId:string|null, lastWrittenAt:number}|null}
 */
export const readDraftSnapshot = (patientId, documentType, identity = {}) => {
  if (Object.keys(identity).length > 0) {
    const selected = selectLatestCompatibleDraftSnapshot(listDraftSnapshots(), {
      ...identity,
      patientId,
      documentType,
    });
    if (!selected || Date.now() - Number(selected.lastWrittenAt) > STALE_AGE_MS) return null;
    return selected;
  }
  const candidates = listDraftSnapshots().filter((snapshot) => {
    if (snapshot.patientId !== patientId || snapshot.documentType !== documentType) return false;
    if (!isSnapshotOwnedByOrganization(snapshot, getCurrentSnapshotOrganizationId())) return false;
    const user = JSON.parse(safeStorage.getItem('evv_user') || '{}');
    if (snapshot.userId && snapshot.userId !== (user.id || user.userId)) return false;
    if (identity.draftId && snapshot.draftId && identity.draftId !== snapshot.draftId) return false;
    if (identity.sessionDate && snapshot.formData?.sessionDate !== identity.sessionDate) return false;
    return true;
  });
  // Never choose a different session/window by recency alone. Keep ambiguous
  // candidates available to recovery tools instead of auto-restoring them.
  const distinct = new Set(candidates.map((s) => JSON.stringify(s.formData)));
  if (distinct.size > 1) return null;
  const key = candidates[0]?.storageKey || getSnapshotKey(patientId, documentType);
  if (!key) return null;
  try {
    const raw = safeStorage.getItem(key);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    if (!stored || !stored.formData || !stored.lastWrittenAt) {
      return null;
    }
    const parsed = { ...stored, formData: restoreSnapshotImages(stored.formData, window.localStorage) };
    // Never restore a browser-local clinical snapshot into another tenant.
    // Legacy snapshots without organization ownership remain stored for their
    // normal prune window but are deliberately not auto-restored.
    if (!isSnapshotOwnedByOrganization(parsed, getCurrentSnapshotOrganizationId())) {
      return null;
    }
    const user = JSON.parse(safeStorage.getItem('evv_user') || '{}');
    if (parsed.userId && parsed.userId !== (user.id || user.userId)) return null;
    if (identity.draftId && parsed.draftId && identity.draftId !== parsed.draftId) return null;
    if (identity.sessionDate && parsed.formData?.sessionDate !== identity.sessionDate) return null;
    if (Date.now() - parsed.lastWrittenAt > STALE_AGE_MS) {
      // Too old for silent in-form restore, but do NOT delete: the login-time
      // sweeper may still upload it to the server. Pruning happens separately
      // at PRUNE_AGE_MS.
      return null;
    }
    return parsed;
  } catch {
    // Leave unreadable content in place for recovery; never delete on read.
    return null;
  }
};

/**
 * Remove a single snapshot. Call after the DB save succeeds — the safety net
 * is no longer needed once the work is durably persisted server-side.
 *
 * @param {string} patientId
 * @param {string} documentType
 */
export const clearDraftSnapshot = (patientId, documentType, acknowledgement = null) => {
  const key = acknowledgement?.key || getSnapshotKey(patientId, documentType);
  if (!key) return;
  try {
    const current = JSON.parse(safeStorage.getItem(key) || 'null');
    // A delayed acknowledgement must never delete edits written after its
    // request began (including edits from another window).
    if (acknowledgement && (!acknowledgement.receipt || current?.receipt !== acknowledgement.receipt)) return false;
    if (current && !isSnapshotOwnedByOrganization(current, getCurrentSnapshotOrganizationId())) return;
    const user = JSON.parse(safeStorage.getItem('evv_user') || '{}');
    if (current?.userId && current.userId !== (user.id || user.userId)) return;
    safeStorage.removeItem(key);
    const removed = safeStorage.getItem(key) === null;
    if (removed) removeUnreferencedSnapshotImages(window.localStorage, (k) => k.startsWith(KEY_PREFIX));
    return removed;
  } catch { /* Preserve unreadable recovery data for investigation. */ }
};

/**
 * Enumerate ALL snapshots regardless of age. Used by the login-time sweeper
 * to find orphaned drafts whose save never reached the server.
 *
 * @returns {Array<{patientId:string, documentType:string, formData:object, patientName:string|null, programId:string|null, lastWrittenAt:number}>}
 */
export const listDraftSnapshots = () => {
  const snapshots = [];
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (!k || !k.startsWith(KEY_PREFIX)) continue;
      try {
        const parsed = JSON.parse(window.localStorage.getItem(k) || '{}');
        if (parsed && parsed.formData && parsed.patientId && parsed.documentType && parsed.lastWrittenAt) {
          snapshots.push({ ...parsed, formData: restoreSnapshotImages(parsed.formData, window.localStorage), storageKey: k });
        }
      } catch {
        // Malformed entry — skip (prune will clean it up)
      }
    }
  } catch {
    // localStorage unavailable — return what we have
  }
  return snapshots;
};

/**
 * Sweep snapshots: drop entries older than PRUNE_AGE_MS, then if there are
 * more than MAX_SNAPSHOTS entries left, drop the oldest until we're under
 * the cap. Called opportunistically on hook mount.
 */
/**
 * One-off conversion of snapshots written before images were stored once.
 * Only the image storage changes: every other field (receipt, timestamps,
 * identity) is kept, so save acknowledgements still match. A snapshot is
 * rewritten only if the converted copy restores to exactly the original
 * form; otherwise the original is put back untouched.
 */
export const compactLegacyDraftSnapshots = () => {
  try {
    const storage = window.localStorage;
    const keys = [];
    for (let i = 0; i < storage.length; i += 1) {
      const k = storage.key(i);
      if (k && k.startsWith(KEY_PREFIX)) keys.push(k);
    }
    for (const key of keys) {
      const raw = storage.getItem(key);
      let parsed;
      try { parsed = JSON.parse(raw || 'null'); } catch { continue; }
      if (!parsed?.formData || !hasInlineImages(parsed.formData)) continue;
      // A tab still running the previous release may restore a recent backup
      // from Recovery history and cannot read image references. Leave anything
      // inside the auto-restore window in the old format.
      if (Date.now() - Number(parsed.lastWrittenAt || 0) < STALE_AGE_MS) continue;
      const compacted = storeSnapshotImages(parsed.formData, storage);
      if (hasInlineImages(compacted)) continue; // some image could not be stored
      if (JSON.stringify(restoreSnapshotImages(compacted, storage)) !== JSON.stringify(parsed.formData)) continue;
      const next = JSON.stringify({ ...parsed, formData: compacted });
      try {
        storage.setItem(key, next);
        if (storage.getItem(key) !== next) storage.setItem(key, raw);
      } catch {
        try { storage.setItem(key, raw); } catch { /* original still in place */ }
      }
    }
    removeUnreferencedSnapshotImages(storage, (k) => k.startsWith(KEY_PREFIX));
  } catch {
    // Storage unavailable: nothing to convert.
  }
};

// Kept for existing callers. Pending edits must not be evicted merely for age
// or capacity: only an acknowledged save or explicit discard may clear them.
export const pruneStaleSnapshots = () => {};

// Completion may clear only snapshots whose content it actually persisted.
// Extra derived fields in the completed payload do not invalidate a match.
export const matchingDraftSnapshotReceipts = (patientId, documentType, formData) =>
  listDraftSnapshots().filter((snapshot) => snapshot.patientId === patientId && snapshot.documentType === documentType &&
    Object.entries(snapshot.formData).every(([key, value]) => JSON.stringify(formData?.[key]) === JSON.stringify(value)))
    .map((snapshot) => ({ key: snapshot.storageKey, receipt: snapshot.receipt }));
